<template>
  <div class="item-management">
    <div class="page-header">
      <div>
        <h2>按次消费商品</h2>
        <p>配置有库存实体物和无库存按次项目，库存与资料权限分开管理。</p>
      </div>
      <button class="primary-btn" @click="openCreateModal">新增项目</button>
    </div>

    <div class="filter-card">
      <select v-model="selectedStoreId" class="form-select" @change="loadItems">
        <option value="">请选择门店</option>
        <option v-for="store in storeList" :key="store.id" :value="store.id">{{ store.storeName }}</option>
      </select>
      <select v-model="filterType" class="form-select" @change="loadItems">
        <option value="">全部类型</option>
        <option value="STOCK">有库存实体物</option>
        <option value="SERVICE">无库存项目</option>
      </select>
      <button class="secondary-btn" @click="loadItems">刷新</button>
    </div>

    <div class="table-card">
      <table>
        <thead>
          <tr>
            <th>图片</th>
            <th>名称</th>
            <th>类型</th>
            <th>金额</th>
            <th>条码</th>
            <th>库存</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="8" class="empty-cell">加载中...</td>
          </tr>
          <tr v-else-if="items.length === 0">
            <td colspan="8" class="empty-cell">暂无项目</td>
          </tr>
          <tr v-else v-for="item in items" :key="item.itemId">
            <td>
              <img v-if="item.imageUrl" :src="normalizeImageUrl(item.imageUrl)" class="item-image" alt="商品图" />
              <span v-else class="image-placeholder">无</span>
            </td>
            <td>{{ item.name }}</td>
            <td>{{ item.itemTypeText }}</td>
            <td>{{ formatMoney(item.price) }}元</td>
            <td>{{ item.barcode || '-' }}</td>
            <td>{{ item.itemType === 'STOCK' ? item.stockQuantity : '-' }}</td>
            <td>{{ item.enabled ? '启用' : '停用' }}</td>
            <td class="actions">
              <button class="link-btn" @click="openEditModal(item)">编辑</button>
              <button v-if="item.itemType === 'STOCK'" class="link-btn" @click="openStockModal(item, 'in')">入库</button>
              <button v-if="item.itemType === 'STOCK'" class="link-btn" @click="openStockModal(item, 'adjust')">调整</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="showItemModal" class="modal-overlay" @click="closeItemModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>{{ editingItem ? '编辑项目' : '新增项目' }}</h3>
          <button class="close-btn" @click="closeItemModal">×</button>
        </div>
        <form @submit.prevent="saveItem">
          <div class="form-grid">
            <label>
              <span>名称</span>
              <input v-model.trim="itemForm.name" class="form-input" required />
            </label>
            <label>
              <span>类型</span>
              <select v-model="itemForm.itemType" class="form-select">
                <option value="STOCK">有库存实体物</option>
                <option value="SERVICE">无库存项目</option>
              </select>
            </label>
            <label>
              <span>金额</span>
              <input v-model.number="itemForm.price" class="form-input" type="number" step="0.01" min="0.01" required />
            </label>
            <label v-if="itemForm.itemType === 'STOCK'">
              <span>条形码</span>
              <input v-model.trim="itemForm.barcode" class="form-input" required />
            </label>
            <label v-if="!editingItem && itemForm.itemType === 'STOCK'">
              <span>初始库存</span>
              <input v-model.number="itemForm.stockQuantity" class="form-input" type="number" min="0" />
            </label>
            <label>
              <span>状态</span>
              <select v-model="itemForm.enabled" class="form-select">
                <option :value="true">启用</option>
                <option :value="false">停用</option>
              </select>
            </label>
          </div>
          <div class="form-group">
            <span>图片</span>
            <div class="upload-row">
              <input ref="imageInput" type="file" accept="image/*" class="hidden-input" @change="uploadImage" />
              <button type="button" class="secondary-btn" @click="$refs.imageInput.click()">上传图片</button>
              <span class="upload-text">{{ itemForm.imageUrl || '未上传' }}</span>
            </div>
          </div>
          <div class="form-group">
            <span>备注</span>
            <textarea v-model.trim="itemForm.remark" class="form-textarea"></textarea>
          </div>
          <div class="modal-actions">
            <button type="button" class="secondary-btn" @click="closeItemModal">取消</button>
            <button type="submit" class="primary-btn" :disabled="saving">{{ saving ? '保存中...' : '保存' }}</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showStockModal" class="modal-overlay" @click="closeStockModal">
      <div class="modal small" @click.stop>
        <div class="modal-header">
          <h3>{{ stockMode === 'in' ? '商品入库' : '库存调整' }}</h3>
          <button class="close-btn" @click="closeStockModal">×</button>
        </div>
        <form @submit.prevent="saveStock">
          <p class="stock-desc">{{ stockItem.name }}，当前库存 {{ stockItem.stockQuantity }}</p>
          <label class="form-group">
            <span>{{ stockMode === 'in' ? '入库数量' : '调整后库存' }}</span>
            <input v-model.number="stockForm.quantity" class="form-input" type="number" min="1" required />
          </label>
          <label class="form-group">
            <span>备注</span>
            <textarea v-model.trim="stockForm.remark" class="form-textarea"></textarea>
          </label>
          <div class="modal-actions">
            <button type="button" class="secondary-btn" @click="closeStockModal">取消</button>
            <button type="submit" class="primary-btn" :disabled="saving">{{ saving ? '提交中...' : '确认' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'ItemManagement',
  data() {
    return {
      storeList: [],
      selectedStoreId: '',
      filterType: '',
      items: [],
      loading: false,
      saving: false,
      showItemModal: false,
      editingItem: null,
      itemForm: this.getEmptyItemForm(),
      showStockModal: false,
      stockItem: null,
      stockMode: 'in',
      stockForm: {
        quantity: 1,
        remark: ''
      }
    }
  },
  mounted() {
    this.loadStores()
  },
  methods: {
    getEmptyItemForm() {
      return {
        storeId: '',
        name: '',
        itemType: 'STOCK',
        price: 0,
        imageUrl: '',
        barcode: '',
        stockQuantity: 0,
        enabled: true,
        remark: ''
      }
    },
    async loadStores() {
      const response = await request.get('/api/stores/getStores', { params: { page: 0, size: 1000 } })
      if (response.data.success) {
        this.storeList = response.data.data.content || []
        if (!this.selectedStoreId && this.storeList.length) {
          this.selectedStoreId = this.storeList[0].id
          this.loadItems()
        }
      }
    },
    async loadItems() {
      if (!this.selectedStoreId) {
        this.items = []
        return
      }
      this.loading = true
      try {
        const response = await request.get('/api/admin/items', {
          params: {
            storeId: this.selectedStoreId,
            itemType: this.filterType || undefined
          }
        })
        if (response.data.success) {
          this.items = response.data.data || []
        } else {
          throw new Error(response.data.message || '加载失败')
        }
      } catch (error) {
        alert(error.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    openCreateModal() {
      if (!this.selectedStoreId) {
        alert('请先选择门店')
        return
      }
      this.editingItem = null
      this.itemForm = this.getEmptyItemForm()
      this.itemForm.storeId = this.selectedStoreId
      this.showItemModal = true
    },
    openEditModal(item) {
      this.editingItem = item
      this.itemForm = {
        storeId: item.storeId,
        name: item.name,
        itemType: item.itemType,
        price: Number(item.price || 0),
        imageUrl: item.imageUrl || '',
        barcode: item.barcode || '',
        stockQuantity: Number(item.stockQuantity || 0),
        enabled: item.enabled !== false,
        remark: item.remark || ''
      }
      this.showItemModal = true
    },
    closeItemModal() {
      if (this.saving) return
      this.showItemModal = false
    },
    async saveItem() {
      if (this.itemForm.itemType === 'STOCK' && !this.itemForm.barcode) {
        alert('有库存商品必须填写条形码')
        return
      }
      this.saving = true
      try {
        const url = this.editingItem ? `/api/admin/items/${this.editingItem.itemId}` : '/api/admin/items'
        const method = this.editingItem ? 'put' : 'post'
        const response = await request[method](url, this.itemForm)
        if (response.data.success) {
          this.showItemModal = false
          await this.loadItems()
        } else {
          throw new Error(response.data.message || '保存失败')
        }
      } catch (error) {
        alert(error.message || '保存失败')
      } finally {
        this.saving = false
      }
    },
    async uploadImage(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      const formData = new FormData()
      formData.append('file', file)
      try {
        const response = await request.post('/api/admin/items/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (response.data.success) {
          this.itemForm.imageUrl = response.data.data
        } else {
          throw new Error(response.data.message || '上传失败')
        }
      } catch (error) {
        alert(error.message || '上传失败')
      } finally {
        event.target.value = ''
      }
    },
    openStockModal(item, mode) {
      this.stockItem = item
      this.stockMode = mode
      this.stockForm = { quantity: mode === 'adjust' ? Number(item.stockQuantity || 0) : 1, remark: '' }
      this.showStockModal = true
    },
    closeStockModal() {
      if (this.saving) return
      this.showStockModal = false
    },
    async saveStock() {
      this.saving = true
      try {
        const path = this.stockMode === 'in' ? 'stock/in' : 'stock/adjust'
        const response = await request.post(`/api/admin/items/${this.stockItem.itemId}/${path}`, this.stockForm)
        if (response.data.success) {
          this.showStockModal = false
          await this.loadItems()
        } else {
          throw new Error(response.data.message || '提交失败')
        }
      } catch (error) {
        alert(error.message || '提交失败')
      } finally {
        this.saving = false
      }
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2)
    },
    normalizeImageUrl(url) {
      if (!url) return ''
      if (/^https?:\/\//i.test(url)) return url
      return `/api/files/${String(url).replace(/^\/?files\//, '').replace(/^\/?/, '')}`
    }
  }
}
</script>

<style scoped>
.item-management {
  padding: 24px;
}
.page-header,
.filter-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}
.page-header h2 {
  margin: 0 0 6px;
}
.page-header p {
  margin: 0;
  color: #64748b;
}
.filter-card,
.table-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}
.form-select,
.form-input,
.form-textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 12px;
}
.form-textarea {
  min-height: 80px;
}
table {
  width: 100%;
  border-collapse: collapse;
}
th,
td {
  padding: 12px;
  border-bottom: 1px solid #eef2f7;
  text-align: left;
}
.empty-cell {
  text-align: center;
  color: #64748b;
}
.item-image {
  width: 52px;
  height: 52px;
  object-fit: cover;
  border-radius: 8px;
}
.image-placeholder {
  color: #94a3b8;
}
.actions {
  display: flex;
  gap: 8px;
}
.primary-btn,
.secondary-btn,
.link-btn {
  border: 0;
  border-radius: 8px;
  padding: 9px 14px;
  cursor: pointer;
}
.primary-btn {
  background: #2563eb;
  color: #fff;
}
.secondary-btn {
  background: #f1f5f9;
  color: #334155;
}
.link-btn {
  background: transparent;
  color: #2563eb;
  padding: 0;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal {
  width: 720px;
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  padding: 22px;
}
.modal.small {
  width: 420px;
}
.modal-header,
.modal-actions,
.upload-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.close-btn {
  border: 0;
  background: transparent;
  font-size: 28px;
  cursor: pointer;
}
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}
.form-group {
  display: block;
  margin-top: 14px;
}
.form-group span,
.form-grid span {
  display: block;
  margin-bottom: 8px;
  color: #475569;
}
.hidden-input {
  display: none;
}
.upload-text,
.stock-desc {
  color: #64748b;
}
</style>
